import { DOMParser } from '@xmldom/xmldom';
import xmlDomUtils from '../../utils/XmlDomUtils';
import { reportError } from '../../../services/ErrorReporting';
import assessmentReader from '../../../store/readHelpers/AssessmentReader';
import questionReader from '../../../store/readHelpers/QuestionReader';
import ckeditorConstants from '../../../constants/CKEditorConstants';
import marketplacePropertiesService from '../../../services/MarketplacePropertiesService';
import sessionReader from '../../../store/readHelpers/SessionReader';

export const helpers = {
    /**
     * Gets the responseVariables that identify expected responses from user interactions.
     * Example for multiple choice, multi response:
     * [{
     *    identifier: 'RESPONSE_1368096164327',
     *    cardinality: 'multiple',
     *    baseType: 'identifier',
     *    choiceSequence: [
     *        'simpleChoice_1368096164453',
     *        'simpleChoice_1368096164457',
     *        'simpleChoice_1368096164459',
     *        'simpleChoice_1368096164456',
     *    ]
     * }]
     *
     * @param doc XML document
     * @returns {Array}
     */
    getResponseIdentifiers(doc) {
        const unWanted = ['numAttempts', 'duration', 'studentHighlights', 'questionAnnotations'];
        const nodes = xmlDomUtils.getChildNodes(doc, 'responseVariable').filter((item) => {
            const attributes = xmlDomUtils.getAttributes(item);
            return unWanted.indexOf(attributes.identifier) === -1;
        });

        const responses = nodes.map((node) => {
            const attributes = xmlDomUtils.getAttributes(node);
            const responseIdentifier = Object.assign({}, attributes);
            if (responseIdentifier.choiceSequence) {
                responseIdentifier.choiceSequence = responseIdentifier.choiceSequence.split(' ');
            }
            responseIdentifier.userResponse = this.getValuesFromResponseVariableNode(node);

            return responseIdentifier;
        });

        return responses;
    },

    getResponseVariableNode(doc, identifier) {
        const nodes = xmlDomUtils.getChildNodes(doc, 'responseVariable').filter((item) => {
            const attributes = xmlDomUtils.getAttributes(item);
            return attributes.identifier === identifier;
        }
        );

        return nodes.length ? nodes[0] : null;
    },

    getValuesFromResponseVariableNode(respVarNode) {
        const responses = xmlDomUtils.getChildNodes(respVarNode, 'candidateResponse');
        const values = xmlDomUtils.getChildNodes(responses[0], 'value')
            .filter(node => node.textContent !== 'null' && node.textContent !== 'null null' && node.textContent !== '')
            .map(node => node.textContent);

        return values;
    },

    getNumAttempts(doc) {
        const respVarNode = this.getResponseVariableNode(doc, 'numAttempts');
        const stringValue = this.getValuesFromResponseVariableNode(respVarNode)[0];
        const intValue = parseInt(stringValue, 10);
        return isNaN(intValue) ? undefined : intValue;
    },

    getDuration(doc) {
        const respVarNode = this.getResponseVariableNode(doc, 'duration');
        const durationStringValue = this.getValuesFromResponseVariableNode(respVarNode)[0];
        const durationInteger = parseInt(durationStringValue, 10);
        return isNaN(durationInteger) ? 0 : durationInteger;
    },

    collectStudentHighlights(doc, itemResult) {
        this.collectAnnotations(doc, itemResult, 'studentHighlights');
    },

    collectQuestionAnnotations(doc, itemResult) {
        this.collectAnnotations(doc, itemResult, 'questionAnnotations');
    },

    collectAnnotations(doc, itemResult, nodeName) {
        const respVarNode = this.getResponseVariableNode(doc, nodeName);
        if (respVarNode) {
            const json = this.getValuesFromResponseVariableNode(respVarNode)[0];
            try {
                itemResult[nodeName] = JSON.parse(json);
            } catch (error) {
                reportError(`Failed to parse ${nodeName} for ${itemResult.identifier}`);
            }
        }
    },

    getOutcomeVariables(doc) {
        const nodes = xmlDomUtils.getChildNodes(doc, 'outcomeVariable');
        const outcomeVariables = {};

        nodes.forEach((node) => {
            const attributes = xmlDomUtils.getAttributes(node);
            const outcomeVar = Object.assign({}, attributes);
            const valueNodes = xmlDomUtils.getChildNodes(node, 'value');

            if (valueNodes.length) {
                outcomeVar.value = valueNodes[0].textContent;
            }

            outcomeVariables[outcomeVar.identifier] = outcomeVar;
        });

        return (Object.keys(outcomeVariables).length > 0) ? outcomeVariables : undefined;
    },

    shouldCheckIndentationInsideAnswer() {
        return !marketplacePropertiesService.isEssayToolbarIndentEnabled() && sessionReader.isContextAnyViewDeliveryType();
    },

    shouldCheckAlignmentInsideAnswer() {
        return !marketplacePropertiesService.isEssayToolbarTextAlignEnabled() && sessionReader.isContextAnyViewDeliveryType();
    },

    responseHasIndentation(identifier, responseIdentifiers) {
        const question = assessmentReader.getQuestion(identifier);
        if (!questionReader.isExtendedText(question)) {
            return false;
        }

        try {
            const indentationReg = /margin-left:(\d+)px/gi;
            return responseIdentifiers
                .map(response => response.userResponse)
                .flat()
                .filter(userResponse => (typeof userResponse) === 'string')
                .some(userResponse => [...userResponse.matchAll(indentationReg)]
                    .map(match => parseInt(match[1], 10))
                    .some(margin => margin > 0 && (margin % ckeditorConstants.DEFAULT_INDENTATION_SIZE === 0))
                );
        } catch (error) {
            console.error('was not able to determinate if ckeditor user response has extra indentation', error);
            return false;
        }
    },

    responseHasAlignment(identifier, responseIdentifiers) {
        const question = assessmentReader.getQuestion(identifier);
        if (!questionReader.isExtendedText(question)) {
            return false;
        }

        try {
            const alignmentReg = /text-align:(right|center|justify)/gi;
            return responseIdentifiers
                .map(response => response.userResponse)
                .flat()
                .filter(userResponse => (typeof userResponse) === 'string')
                .some(userResponse => alignmentReg.test(userResponse));
        } catch (error) {
            console.error('was not able to determinate if ckeditor user response has extra alignment', error);
            return false;
        }
    },
};

function parseItemResultXml(xmlString) {
    const doc = new DOMParser().parseFromString(xmlString);
    const attributes = xmlDomUtils.getAttributes(doc.firstChild);
    const itemResult = Object.assign({}, attributes);
    const outcomeVariables = helpers.getOutcomeVariables(doc);

    itemResult.identifier = parseInt(itemResult.identifier, 10);
    itemResult.sequenceIndex = parseInt(itemResult.sequenceIndex, 10);

    if (itemResult.datestamp.length === 19) {
        itemResult.datestamp = `${itemResult.datestamp}Z`;
    }

    itemResult.numAttempts = helpers.getNumAttempts(doc);
    itemResult.duration = helpers.getDuration(doc);
    itemResult.responseIdentifiers = helpers.getResponseIdentifiers(doc);
    if (outcomeVariables) {
        itemResult.outcomeVariables = outcomeVariables;
    }

    if (helpers.shouldCheckIndentationInsideAnswer()) {
        itemResult.hasIndentation = helpers.responseHasIndentation(itemResult.identifier, itemResult.responseIdentifiers);
    }

    if (helpers.shouldCheckAlignmentInsideAnswer()) {
        itemResult.hasAlignment = helpers.responseHasAlignment(itemResult.identifier, itemResult.responseIdentifiers);
    }

    helpers.collectStudentHighlights(doc, itemResult);
    helpers.collectQuestionAnnotations(doc, itemResult);

    return itemResult;
}

export default parseItemResultXml;
